import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Globe2, Mail, Percent, Save, RefreshCw, MapPin, Settings2, Image as ImageIcon, UploadCloud } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useFontSize } from './FontSizeContext';
import EmailSettingsModal from './EmailSettings';
import UniversalLookup from './UniversalLookup';
import COUNTRY_COORDINATES from './countryCoordinates';
import { useSystemDefaultLogo, GLOBAL_ORG_ID } from './useSystemDefaultLogo';

const SystemSettings = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';
  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;

  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [countryLookupOpen, setCountryLookupOpen] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoFileRef = useRef(null);
  const systemLogo = useSystemDefaultLogo();

  const [general, setGeneral] = useState({ DEFAULT_COUNTRY: '', DEFAULT_LAT: '', DEFAULT_LNG: '' });
  const [tax, setTax] = useState({ DEFAULT_TAXRATE: '' });

  const token = localStorage.getItem('token');
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const fetchGroup = useCallback(async (appName, setter) => {
    try {
      // 🎯 التوكن لازم يتبعت - إعدادات النظام بقت بصلاحية system-settings
      const res = await axios.get(`/api/admin/system-settings?org_id=${GLOBAL_ORG_ID}&app_name=${appName}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const obj = {};
      (res.data || []).forEach((item) => { obj[item.setting_key] = item.setting_value; });
      setter((prev) => ({ ...prev, ...obj }));
    } catch (err) {
      console.error(`Failed to load ${appName} global settings`, err);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchGroup('GENERAL', setGeneral),
      fetchGroup('TAX', setTax),
    ]).finally(() => setLoading(false));
  }, [fetchGroup]);

  const saveGroup = async (appName, values) => {
    setSaving(true);
    try {
      await axios.post('/api/admin/save-system-settings', {
        org_id: GLOBAL_ORG_ID,
        app_name: appName,
        settings: values,
      }, authHeaders);
      toast.success(t('systemSettings.saveSuccess', 'Saved successfully'));
    } catch (err) {
      toast.error(t('systemSettings.saveFailed', 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  // 🎯 اليوزر مش المفروض يدخل Lat/Lng يدوي خالص - لما يختار دولة من نفس
  // الدومين COUNTRY المستخدم في باقي الشاشات (شركات/جهات اتصال)، بنجيب
  // إحداثياتها تلقائيًا من جدول ثابت (countryCoordinates.js) ونحطها كجزء
  // من نفس عملية الحفظ من غير أي تدخل تاني
  const handleCountrySelect = (item) => {
    const countryValue = item?.value || '';
    setCountryLookupOpen(false);
    if (!countryValue) return;
    const coords = COUNTRY_COORDINATES[countryValue];
    setGeneral((prev) => ({
      ...prev,
      DEFAULT_COUNTRY: countryValue,
      DEFAULT_LAT: coords ? coords[0] : prev.DEFAULT_LAT,
      DEFAULT_LNG: coords ? coords[1] : prev.DEFAULT_LNG,
    }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    setUploadingLogo(true);
    try {
      await axios.post(`/api/organizations/${GLOBAL_ORG_ID}/upload-logo`, formData, {
        headers: { ...authHeaders.headers, 'Content-Type': 'multipart/form-data' },
      });
      systemLogo.refresh();
      toast.success(t('systemSettings.saveSuccess', 'Saved successfully'));
    } catch (err) {
      toast.error(t('systemSettings.saveFailed', 'Save failed'));
    } finally {
      setUploadingLogo(false);
      if (logoFileRef.current) logoFileRef.current.value = '';
    }
  };

  return (
    <div className={`p-8 bg-[#f3f4f6] min-h-screen font-black uppercase text-[0.85em] ${isRTL ? 'text-right' : 'text-left'}`} style={{ fontSize: `${fontSize}px` }} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-4xl mx-auto space-y-6">

        {/* Header Card */}
        <div className="bg-white rounded shadow-xl p-8 border-t-4 border-[#1a3353]">
          <div className="flex justify-between items-center border-b pb-6 mb-8">
            <div className="flex items-center gap-4">
              <div className="bg-blue-100 p-3 rounded-lg text-[#1a3353]">
                <Settings2 size={24} />
              </div>
              <div>
                <h2 className="text-[1.38em] text-[#1a3353] font-black tracking-tighter normal-case">{t('systemSettings.title', 'System Settings')}</h2>
                <p className="text-gray-400 text-[0.69em] normal-case font-semibold">{t('systemSettings.subtitle', 'Global defaults shared across every organization')}</p>
              </div>
            </div>
            {loading && <RefreshCw size={18} className="animate-spin text-gray-400" />}
          </div>

          {/* Tabs */}
          <div className="flex gap-2 border-b mb-6">
            <button
              onClick={() => setActiveTab('general')}
              className={`px-4 py-2 flex items-center gap-2 border-b-2 transition-colors ${activeTab === 'general' ? 'border-[#1a3353] text-[#1a3353]' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
            >
              <Globe2 size={14} /> {t('systemSettings.tabs.general', 'General & Map')}
            </button>
            <button
              onClick={() => setActiveTab('email')}
              className={`px-4 py-2 flex items-center gap-2 border-b-2 transition-colors ${activeTab === 'email' ? 'border-[#1a3353] text-[#1a3353]' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
            >
              <Mail size={14} /> {t('systemSettings.tabs.email', 'Default Email')}
            </button>
            <button
              onClick={() => setActiveTab('tax')}
              className={`px-4 py-2 flex items-center gap-2 border-b-2 transition-colors ${activeTab === 'tax' ? 'border-[#1a3353] text-[#1a3353]' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
            >
              <Percent size={14} /> {t('systemSettings.tabs.tax', 'Default Tax')}
            </button>
          </div>

          {activeTab === 'general' && (
            <div className="space-y-8">
              {/* System Logo */}
              <div>
                <div className="flex items-center gap-6 pb-6 border-b">
                  <div className="w-24 h-24 rounded-lg border-2 border-dashed border-gray-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
                    {systemLogo.logoUrl ? (
                      <img src={systemLogo.logoUrl} alt="System logo" className="w-full h-full object-contain" />
                    ) : (
                      <ImageIcon size={28} className="text-gray-300" />
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="font-black text-gray-500 uppercase tracking-tighter text-[11px]">{t('systemSettings.fields.logo', 'System Default Logo')}</label>
                    <p className="text-[10px] text-gray-400 font-semibold normal-case max-w-md">{t('systemSettings.logoHint', 'Used for any organization that has not uploaded its own logo yet.')}</p>
                    <button type="button" onClick={() => logoFileRef.current?.click()} disabled={uploadingLogo}
                      className="flex items-center gap-2 w-fit px-4 py-1.5 border border-[#1a3353] text-[#1a3353] font-black text-[10px] uppercase hover:bg-[#1a3353] hover:text-white transition-all disabled:opacity-50">
                      {uploadingLogo ? <RefreshCw size={14} className="animate-spin" /> : <UploadCloud size={14} />}
                      {t('systemSettings.uploadLogo', 'Upload Logo')}
                    </button>
                    <input ref={logoFileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogoUpload} />
                  </div>
                </div>
              </div>

              {/* Default Country / Map */}
              <div className="space-y-6">
                <div className="p-4 bg-blue-50/50 rounded-xl border border-blue-100 flex gap-3 items-start">
                  <MapPin size={16} className="text-blue-600 shrink-0 mt-0.5" />
                  <p className={`text-[10px] text-blue-900 font-bold leading-relaxed normal-case ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('systemSettings.mapHint', 'Used as the map center/fallback for any site or location that has no coordinates saved yet.')}
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <div className={`flex flex-col gap-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                    <label className="text-[10px] font-black text-gray-500 uppercase italic">{t('systemSettings.fields.country', 'Default Country')}</label>
                    <button
                      type="button"
                      onClick={() => setCountryLookupOpen(true)}
                      className={`w-full py-2.5 px-4 bg-slate-50 border border-slate-200 rounded-xl outline-none hover:border-blue-500 hover:bg-white transition-all font-bold text-sm text-blue-900 normal-case flex items-center justify-between ${isRTL ? 'text-right' : 'text-left'}`}
                    >
                      <span>{general.DEFAULT_COUNTRY || t('systemSettings.selectCountry', 'Select Country')}</span>
                      <Globe2 size={14} className="text-slate-400 shrink-0" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <button onClick={() => saveGroup('GENERAL', general)} disabled={saving} className="flex items-center gap-2 px-10 py-2 bg-[#1a3353] text-white rounded-xl text-[10px] font-black hover:bg-black shadow-xl shadow-blue-900/20 transition-all disabled:opacity-50 active:scale-95">
                  {saving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
                  {t('common.save', 'Save')}
                </button>
              </div>
            </div>
          )}

          {activeTab === 'email' && (
            <div className="space-y-6">
              <div className="p-4 bg-orange-50 rounded-xl border border-orange-100 flex gap-3 items-start">
                <Mail size={16} className="text-orange-600 shrink-0 mt-0.5" />
                <p className={`text-[10px] text-orange-900 font-bold leading-relaxed normal-case ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('systemSettings.emailHint', 'Any organization that has not configured its own SMTP settings will automatically use these defaults instead.')}
                </p>
              </div>
              <div className="flex justify-end">
                <button onClick={() => setShowEmailModal(true)} className="flex items-center gap-2 px-10 py-2 bg-[#1a3353] text-white rounded-xl text-[10px] font-black hover:bg-black shadow-xl shadow-blue-900/20 transition-all active:scale-95">
                  <Mail size={14} /> {t('systemSettings.configureEmail', 'Configure Default Email')}
                </button>
              </div>
            </div>
          )}

          {activeTab === 'tax' && (
            <div className="space-y-6">
              <div className="p-4 bg-green-50/50 rounded-xl border border-green-100 flex gap-3 items-start">
                <Percent size={16} className="text-green-600 shrink-0 mt-0.5" />
                <p className={`text-[10px] text-green-900 font-bold leading-relaxed normal-case ${isRTL ? 'text-right' : 'text-left'}`}>
                  {t('systemSettings.taxHint', 'Used to create the first default tax category for any newly created organization. Existing organizations are never changed.')}
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <GeneralInput label={t('systemSettings.fields.taxRate', 'Default Tax Rate (%)')} value={tax.DEFAULT_TAXRATE} onChange={(v) => setTax({ ...tax, DEFAULT_TAXRATE: v })} isRTL={isRTL} type="number" />
              </div>
              <div className="flex justify-end">
                <button onClick={() => saveGroup('TAX', tax)} disabled={saving} className="flex items-center gap-2 px-10 py-2 bg-[#1a3353] text-white rounded-xl text-[10px] font-black hover:bg-black shadow-xl shadow-blue-900/20 transition-all disabled:opacity-50 active:scale-95">
                  {saving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
                  {t('common.save', 'Save')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <EmailSettingsModal isOpen={showEmailModal} onClose={() => setShowEmailModal(false)} orgId={GLOBAL_ORG_ID} />
      <UniversalLookup
        isOpen={countryLookupOpen}
        onClose={() => setCountryLookupOpen(false)}
        title={t('systemSettings.selectCountry', 'Select Country')}
        domainId="COUNTRY"
        onSelect={handleCountrySelect}
      />
    </div>
  );
};

const GeneralInput = ({ label, value, onChange, type = 'text', isRTL }) => (
  <div className={`flex flex-col gap-1 ${isRTL ? 'text-right' : 'text-left'}`}>
    <label className="text-[10px] font-black text-gray-500 uppercase italic">{label}</label>
    <input
      type={type}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      dir="ltr"
      className={`w-full py-2.5 px-4 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-blue-500 focus:bg-white transition-all font-bold text-sm text-blue-900 normal-case ${isRTL ? 'text-right' : 'text-left'}`}
    />
  </div>
);

export default SystemSettings;
